/**
 * 侧边栏导航结构 —— 严格照抄 docs/03-UIUX.md §4.1 的六组划分与
 * docs/06-ICON-MAP.md §5 的图标映射。分组依据是「改变频率 + 风险等级」，
 * 不是数据表罗列；「安全」组与日常操作物理隔开，本身就是一层防误触设计。
 *
 * `implemented: false` 的条目会渲染成占位页并在导航上标注「未实现」——
 * 让一个死链看起来像能用，比缺功能更糟。
 */
import type { IconName } from '../icons/registry';

export interface NavItem {
  readonly label: string;
  readonly path: string;
  readonly icon: IconName;
  readonly implemented: boolean;
  /** 占位页说明这个页面将承担什么，避免占位页变成无意义空壳 */
  readonly purpose: string;
}

export interface NavGroup {
  readonly title: string;
  readonly items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    title: '概览',
    items: [
      {
        label: '运行概览',
        path: '/',
        icon: 'layout-dashboard',
        implemented: true,
        purpose: '库存水位、待处理异常、近 24h 验证摘要与服务健康。',
      },
    ],
  },
  {
    title: '目录',
    items: [
      {
        label: '产品',
        path: '/products',
        icon: 'package',
        implemented: true,
        purpose: '产品列表与详情（版本、套餐、签名密钥、公钥分发、归档）。',
      },
      {
        label: '套餐与权益',
        path: '/plans',
        icon: 'layers',
        implemented: true,
        purpose: '权益矩阵编辑，保存前显示影响面预览（T2）。',
      },
      {
        label: '功能开关注册表',
        path: '/entitlements',
        icon: 'sliders-horizontal',
        implemented: true,
        purpose: '定义「能授予什么能力」的全局边界，新增开关走 T2。',
      },
    ],
  },
  {
    title: '发行',
    items: [
      {
        label: '授权码批次',
        path: '/batches',
        icon: 'boxes',
        implemented: true,
        purpose: '批次列表、三步生成向导、一次性 CSV 导出。',
      },
      {
        label: '库存查询',
        path: '/codes',
        icon: 'scan-barcode',
        implemented: true,
        purpose: '跨批次授权码检索与批量作废（T2）。服务端不存明文，只能按后 4 位检索。',
      },
      {
        label: '授权',
        path: '/licenses',
        icon: 'file-key',
        implemented: true,
        purpose: '正式授权的列表、详情、生命周期操作。',
      },
      {
        label: '激活设备',
        path: '/activations',
        icon: 'monitor-smartphone',
        implemented: true,
        purpose: '跨授权设备表，解绑（T1）与换机（T2）。',
      },
      {
        label: '客户与订单',
        path: '/customers',
        icon: 'contact',
        implemented: true,
        purpose: '客户列表与其订单/授权。客户是可选实体，未登记显示为 --meta 文案。',
      },
    ],
  },
  {
    title: '安全',
    items: [
      {
        label: '签名密钥',
        path: '/signing-keys',
        icon: 'key-round',
        implemented: true,
        purpose: '按产品分组的密钥卡片、轮换（T3）、私钥一次性展示。',
      },
      {
        label: '管理员',
        path: '/admins',
        icon: 'user-cog',
        implemented: true,
        purpose: '管理员与角色分配；不允许停用最后一个 owner。',
      },
      {
        label: 'API Key',
        path: '/admins',
        icon: 'terminal',
        implemented: true,
        purpose: '作用域矩阵、明文一次性展示、撤销（T2）。',
      },
      {
        label: '管理员会话',
        path: '/admins',
        icon: 'monitor',
        implemented: true,
        purpose: '在线会话与踢下线（T1）；当前会话不可踢自己。',
      },
      {
        label: 'IP 黑名单',
        path: '/security',
        icon: 'ban',
        implemented: true,
        purpose: '查看自动封禁的攻击源 IP（限流/接口刷屏/登录爆破），owner 可人工解封，防止误封锁门。',
      },
    ],
  },
  {
    title: '审计',
    items: [
      {
        label: '审计日志',
        path: '/audit',
        icon: 'scroll-text',
        implemented: true,
        purpose: '只读流水，游标分页，失败事件行首红色标记。',
      },
      {
        label: '兑换请求',
        path: '/redemptions',
        icon: 'repeat',
        implemented: true,
        purpose: '只读幂等记录，排查同一码被多次尝试兑换。',
      },
    ],
  },
  {
    title: '系统',
    items: [
      {
        label: '健康与备份',
        path: '/system',
        icon: 'activity',
        implemented: true,
        purpose: 'API / DB / Queue / Signer 健康，备份时间与校验状态。',
      },
    ],
  },
];

export const ALL_NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

export function findNavItem(pathname: string): NavItem | undefined {
  if (pathname === '/') return ALL_NAV_ITEMS.find((item) => item.path === '/');
  return ALL_NAV_ITEMS.filter((item) => item.path !== '/').find((item) =>
    pathname.startsWith(item.path),
  );
}
